import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IonNav, ModalController } from '@ionic/angular';
import { CaseService } from 'src/app/services/case.service';
import { CommonService } from 'src/app/services/common.service';
import { AddressDetailsPage } from 'src/app/master/address-details/address-details.page';

interface RepayApiResponse {
    result: string;
    message: string;
}

interface RepayApiPaymentResponse {
    result: string;
    result_text: string;
    message?: string;
}

@Component({
  selector: 'app-repay',
  templateUrl: './repay.page.html',
  styleUrls: ['./repay.page.scss'],
})
export class RepayPage implements OnInit {
  @Input() caseId;
  @Input() responseData;
  @Input() debtorDetails;
  title = 'Lateral Payment Portal';
  repayForm: FormGroup;
  saveBtnDisabled = false;
  isConfigured = false;
  amount = 0;
  fieldLabels = {};
  checkoutFormId: string;
  paytoken: string;
  years: number[] = this.generateYearList();
  months: number[] = this.generateMonthList();
  card_expiration: string;
  waive_conv_fee = true;

  constructor(
    private formBuilder: FormBuilder,
    private caseService: CaseService,
    private commonService: CommonService,
    private ionNav: IonNav,
    private modalController: ModalController
  ) {}

  ngOnInit() {
    this.saveBtnDisabled = false;
    this.amount = parseFloat(this.responseData?.net_amount);
    let portalSettings = JSON.parse(localStorage.getItem('portal_settings'));

    if (portalSettings?.payment_type) {
      let payment_type = portalSettings?.payment_type
        ? portalSettings?.payment_type
        : {};

      if (payment_type?.repay && payment_type?.repay?.label) {
        this.title = payment_type.repay.label;
      }
    }

    if (portalSettings?.fieldLabels) {
      this.fieldLabels = portalSettings.fieldLabels;
    }

    this.isConfigured = true;
    this.initForm();
  }

  initForm() {
    this.repayForm = this.formBuilder.group({
      amount: [
        { value: this.amount ? this.amount : 0, disabled: true },
        [Validators.required],
      ],
      cardholder_name: [
        this.debtorDetails?.debtor_1_name
          ? `${this.debtorDetails?.debtor_1_name} ${this.debtorDetails?.debtor_1_surname}`
          : '',
        [Validators.required],
      ],
      card_number: [
        '',
        [
          Validators.required,
          Validators.pattern('^[0-9]{12,16}$'),
          Validators.maxLength(16),
        ],
      ],
      card_expiry_month: ['', [Validators.required]],
      card_expiry_year: ['', [Validators.required]],
      card_cvc: [
        '',
        [
          Validators.required,
          Validators.pattern('^[0-9]{3}$'),
          Validators.maxLength(3),
        ],
      ],
    });
  }

  generateYearList(): number[] {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 16 }, (_, index) => currentYear + index);

    return years;
  }

  generateMonthList(): number[] {
    return Array.from({ length: 12 }, (_, index) => index + 1);
  }

  async getCheckoutFormId() {
    if (this.repayForm.valid) {
      this.saveBtnDisabled = true;
      this.commonService.showLoader();

      const checkoutFormData = {
        payment_method: 'card',
        source: 'Lateral DAPI',
      };

      try {
        const response = (await this.caseService
          .getRepayCheckoutForm(checkoutFormData)
          .toPromise()) as RepayApiResponse;
        if (response?.result) {
          this.checkoutFormId = response.result;
          await this.getPayToken();
        }
        else {
          console.log(response);
          this.commonService.showToast('Failed to get checkout form', 'danger');
          this.saveBtnDisabled = false;
          this.commonService.dismissLoader();
        }
      }
      catch (error) {
        this.commonService.showToast('Error getting checkout form', 'danger');
        this.saveBtnDisabled = false;
        this.commonService.dismissLoader();
      }
    }
  }

  async getPayToken() {
    try {
      const month = this.repayForm.value.card_expiry_month
        .toString()
        .padStart(2, '0');
      const year = this.repayForm.value.card_expiry_year.toString().slice(-2);
      this.card_expiration = `${month}${year}`;

      const payload = {
        checkout_form_id: this.checkoutFormId,
        amount: this.amount,
        transaction_type: 'sale',
        customer_id: this.debtorDetails?.debtor_id,
        cardholder_name: this.debtorDetails?.debtor_1_name + ' ' + this.debtorDetails?.debtor_1_surname,
        card_number: this.repayForm.value.card_number,
        card_expiration: this.card_expiration,
        card_cvc: this.repayForm.value.card_cvc,
        address_street: this.debtorDetails?.addresses[0].address_ln1,
        address_city: this.debtorDetails?.addresses[0].address_ln3,
        address_state: this.debtorDetails?.addresses[0].address_town,
        address_zip: this.debtorDetails?.addresses[0].address_postcode,
        payment_channel: 'web',
        source: 'Lateral DAPI',
        channel_user: this.debtorDetails?.emails[0].account,
        save_payment_method: false,
        case_id: this.caseId,
        waive_conv_fee: this.waive_conv_fee || true,
      };

      const response = (await this.caseService
        .getRepayPayToken(payload)
        .toPromise()) as RepayApiResponse;

      if (response?.result) {
        this.paytoken = response.result;
        await this.processPayment();
      }
      else {
        console.log(response);
        this.commonService.showToast('Failed to get payment token', 'danger');
        this.saveBtnDisabled = false;
        this.commonService.dismissLoader();
      }
    }
    catch (error) {
      this.commonService.showToast('Error getting payment token', 'danger');
      this.saveBtnDisabled = false;
      this.commonService.dismissLoader();
    }
  }

  async processPayment() {
    try {
      const paymentData = {
        amount: this.amount,
        transaction_type: 'sale',
        customer_id: this.debtorDetails?.debtor_id,
        cardholder_name:this.debtorDetails?.debtor_1_name + ' ' + this.debtorDetails?.debtor_1_surname,
        card_number: this.repayForm.value.card_number,
        card_cvc: this.repayForm.value.card_cvc,
        card_expiration: this.card_expiration,
        address_street: this.debtorDetails?.addresses[0].address_ln1,
        address_city: this.debtorDetails?.addresses[0].address_ln3,
        address_state: this.debtorDetails?.addresses[0].address_town,
        address_zip: this.debtorDetails?.addresses[0].address_postcode,
        paytoken: this.paytoken,
        sec_code: 'TEL',
        source: 'Lateral DAPI',
        waive_conv_fee: this.waive_conv_fee || true,
        payment_channel: 'web',
        channel_user: this.debtorDetails?.emails[0].account,
        save_payment_method: false,
        checkout_form_id: this.checkoutFormId,
        case_id: this.caseId,
      };

      const response = (await this.caseService
        .makeRepayPayment(this.caseId, paymentData)
        .toPromise()) as RepayApiPaymentResponse;
      this.saveBtnDisabled = false;
      this.commonService.dismissLoader();

      if (response?.result == '0') {
        this.commonService.showToast(
          response?.message || 'Payment successful',
          'success'
        );
        this.caseService.saveCaseDetails(localStorage.getItem('debtor_id'));
        this.ionNav.popToRoot();
      } else {
        this.commonService.showToast(
          response?.message || 'Payment failed',
          'danger'
        );
      }
    } catch (error) {
      this.commonService.showToast('Error processing payment', 'danger');
      this.saveBtnDisabled = false;
      this.commonService.dismissLoader();
    }
  }

  async dismiss() {
    this.ionNav.pop();
  }

  getBillingAddress(add) {
    const address_ln1 = add?.address_ln1 ? add?.address_ln1 + ', ' : '';
    const address_ln2 = add?.address_ln2 ? add?.address_ln2 + ', ' : '';
    const address_ln3 = add?.address_ln3 ? add?.address_ln3 + ', ' : '';
    const address_town = add?.address_town ? add?.address_town + ', ' : '';
    const address_postcode = add?.address_postcode ? add?.address_postcode : '';
    return (
      address_ln1 + address_ln2 + address_ln3 + address_town + address_postcode
    );
  }

  isAddressMissing() {
    const value = this.repayForm.value;
    if (
      !value['address_town'] ||
      !value['address_postcode'] ||
      !value['address_ln1']
    )
      return true;
    return false;
  }

  async changeAddress() {
    const address = {
      address_ln1: this.debtorDetails?.addresses[0]?.address_ln1 || '',
      address_ln2: this.debtorDetails?.addresses[0]?.address_ln2 || '',
      address_ln3: this.debtorDetails?.addresses[0]?.address_ln3 || '',
      address_town: this.debtorDetails?.addresses[0]?.address_town || '',
      address_postcode: this.debtorDetails?.addresses[0]?.address_postcode || '',
    };
    const requiredAddress = {
      address_ln1: true,
      address_town: true,
      address_postcode: true,
    };
    const modal = await this.modalController.create({
      component: AddressDetailsPage,
      componentProps: {
        address,
        requiredAddress,
        setAddress: (data) => {
          if (this.debtorDetails?.addresses && this.debtorDetails.addresses.length > 0) {
            this.debtorDetails.addresses[0] = {
              ...this.debtorDetails.addresses[0],
              ...data
            };
          }
        },
      },
    });
    return await modal.present();
  }
}